import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import { ReactComponent as ActiveIcon } from 'assets/hotspotTeaser/hotspot-icon-active.svg'
import { ReactComponent as DefaultIcon } from 'assets/hotspotTeaser/hotspot-icon-default.svg'
import { ReactComponent as Hover } from 'assets/hotspotTeaser/hotspot-icon-hover.svg'
import useContentPosition, { HotspotPosition } from './useContentPosition'
import { Product } from 'utils/productListFetcher/types'
import Link from 'theme/atoms/Link'
import Flags from 'theme/molecules/Flags'
import useTranslations from 'hooks/useTranslations'
import { ReactComponent as LinkArrow } from 'assets/link-arrow.svg'
import HotspotItemPrice from './HotspotItemPrice'

type Props = {
  posX: number
  posY: number
  dotSize: number
  active: boolean
  productData: Product | null
  setActive: () => void
}

export default function HotspotItem(props: Props) {
  const [contentRef, hotspotRef, tooltipPositionStyle] =
    useContentPosition(props)
  const t = useTranslations<'molecules-HotspotTeaser'>()

  return (
    <Wrapper
      $posX={props.posX}
      $posY={props.posY}
      $dotSize={props.dotSize}
      $active={props.active}
      className="HotspotItem"
      onClick={props.setActive}
      ref={hotspotRef}
      data-cy-handle="hotspot-item"
    >
      <div className="hotspot-icon">
        <ActiveIcon className="icon-active" />
        <DefaultIcon className="default" />
        <Hover className="hover" />
      </div>
      <ProductData
        ref={contentRef}
        $position={tooltipPositionStyle}
        className="content"
        data-cy-state="hotspot-item-content"
        as={props.productData ? Link : undefined}
        to={
          props.productData
            ? `/pdp/${props.productData.containerID}/?sku=${props.productData.sku}`
            : undefined
        }
        data-cy-handle="product-link"
      >
        {props.productData && (
          <>
            <div className="product-info">
              <div className="flag-wrapper">
                <Flags
                  product={props.productData}
                  showOnlyDiscount
                  type={'widget'}
                />
              </div>
              <div className="brand">{props.productData.brand}</div>
              <div className="title">{props.productData.title}</div>
              <HotspotItemPrice productData={props.productData} />
            </div>

            <Link className="label">
              {t('to_product')} <LinkArrow />
            </Link>
          </>
        )}
      </ProductData>
    </Wrapper>
  )
}
const ProductData = styled.div<{ $position: HotspotPosition }>`
  display: none;
  background-color: ${theme.colors.white};
  position: absolute;
  width: 10rem;
  @media (min-width: ${theme.ms.MD}px) {
    width: 13.75rem;
  }
  padding: ${theme.spacing('s')};
  flex-direction: column;
  justify-content: space-between;

  z-index: 1;
  ${(props) => props.$position}
  > .product-info {
    display: flex;
    flex-direction: column;
    > .brand {
      ${theme.ty('r-s', '700', 'rc-base')}
      color: ${theme.colors.shade.b2};
    }
    > .title {
      ${theme.ty('r-s', '400', 'r-base')}
      color: ${theme.colors.b0};
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    > .subtitle {
      ${theme.ty('r-xs', '400', 'r-s')}
      color: ${theme.colors.shade.b2};
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  > .label {
    margin-top: ${theme.spacing('xs')};
    display: flex;
    gap: ${theme.spacing('xxs')};
    ${theme.ty('r-s', '700', 'rc-base')}
    color: ${theme.colors.b0};
    > svg {
      color: ${theme.colors.black};
      #Path-5,
      #Path-6 {
        color: ${theme.colors.black};
        stroke: ${theme.colors.black};
      }
      g {
        color: ${theme.colors.black};
        stroke: ${theme.colors.black};
        filter: none;
      }
    }
  }
`
export const Wrapper = styled.div<{
  $posX: number
  $posY: number
  $dotSize: number
  $active: boolean
}>`
  cursor: pointer;
  position: absolute;
  left: ${(props) => `${props.$posX / 10}%`};
  top: ${(props) => `${props.$posY / 10}%`};
  width: ${(props) => `${props.$dotSize}%`};
  height: ${(props) => `${props.$dotSize}%`};
  margin-left: ${(props) => `-${props.$dotSize / 2}%`};
  margin-top: ${(props) => `-${props.$dotSize / 2}%`};
  overflow: unset;

  &:hover {
    > .hotspot-icon {
      > .hover {
        ${(props) => !props.$active && `display: block;`}
      }
      > .icon-active {
        ${(props) => !props.$active && `display: none;`}
      }
      > .default {
        ${(props) => !props.$active && `display: none;`}
      }
    }
  }

  > .content {
    ${(props) => props.$active && `display: flex;`}
  }

  > .hotspot-icon {
    width: 100%;
    height: 100%;

    > svg {
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
    }
    > .icon-active {
      display: ${(props) => (props.$active ? 'block' : 'none')};
    }
    > .default {
      display: ${(props) => (props.$active ? 'none' : 'block')};
    }
    > .hover {
      display: none;
    }
  }
`
